import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Upload, FileText, Undo2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function parseCSV(text) {
  // strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const stripQ = (s) => s.replace(/^"(.*)"$/, "$1").trim();
  const headers = lines[0].split(",").map(h => stripQ(h).toLowerCase());
  const BOOL_KEYS = new Set(["is_qualified","notify_enabled","is_club_member","available_saturday","available_sunday","send_welcome_email"]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue; // skip empty rows
    const cols = line.split(",").map(stripQ);
    const obj = {};
    headers.forEach((h, idx) => {
      const v = cols[idx];
      if (v !== undefined && v !== "") obj[h] = v;
    });
    if (Object.keys(obj).length === 0) continue; // skip all-empty rows
    if (obj.start_hour) obj.start_hour = parseInt(obj.start_hour);
    if (obj.end_hour) obj.end_hour = parseInt(obj.end_hour);
    BOOL_KEYS.forEach(k => {
      if (obj[k] !== undefined) obj[k] = String(obj[k]).toLowerCase() !== "false";
    });
    rows.push(obj);
  }
  return rows;
}

const CONFIGS = {
  fixtures: {
    label: "Fixtures / Bookings",
    endpoint: "/admin/fixtures/upload",
    template: "pitch_name,booking_date,start_hour,end_hour,session_type,match_type,opponent,referee_name,team_name,notes\nMain Ground (Grass),2026-03-07,10,12,game,league,Stansted YFC U12,David Miller,,Home fixture\nTraining Field B,2026-03-08,17,19,training,,,,U10 Lions,Weekly training",
    filename: "fixtures-template.csv",
    required: "pitch_name, booking_date (YYYY-MM-DD), start_hour, end_hour",
    optional: "session_type (training|game), match_type (league|cup|friendly), opponent, referee_name, team_name, notes",
    normalize: (r) => ({ ...r, session_type: r.session_type || "game" }),
  },
  teams: {
    label: "Teams",
    endpoint: "/admin/upload/teams",
    template: "name,age_group,coach_name,squad,game_format,color,league_name\nU10 Lions,U10,Steve Rogers,First Team,7v7,Yellow,East Herts Youth League\nU12 Eagles,U12,Sarah Bell,First Team,9v9,Blue,East Herts Youth League\nU14 Wolves,U14,,Reserves,11v11,Green,",
    filename: "teams-template.csv",
    required: "name, age_group",
    optional: "coach_name, squad, game_format (3v3|5v5|7v7|9v9|11v11), color (Black|White|Green|Yellow|Blue), league_name (auto-created if missing)",
    normalize: (r) => r,
  },
  coaches: {
    label: "Coaches",
    endpoint: "/admin/upload/coaches",
    template: "name,email,phone,team_name,password\nSteve Rogers,steve@example.com,07700900801,U10 Lions,\nSarah Bell,sarah@example.com,07700900802,U12 Eagles,\nAlex Rice,alex@example.com,,,",
    filename: "coaches-template.csv",
    required: "name, email",
    optional: "phone, team_name (auto-links as team owner), password (leave blank to auto-generate)",
    normalize: (r) => r,
  },
  referees: {
    label: "Referees",
    endpoint: "/admin/upload/referees",
    template: "name,email,phone,level,is_qualified,notify_enabled,is_club_member,max_age_group,available_saturday,available_sunday,parent_name,parent_phone,parent_email\nDavid Miller,david@example.com,07700900101,FA Level 7,true,true,true,,true,true,,,\nSarah Thompson,sarah.t@example.com,07700900202,FA Level 6,false,true,false,U12,true,false,,,\nJamie Cope,jamie@example.com,07700900303,Youth Ref,false,true,true,U14,true,true,Matt Cope,07700900304,matt@example.com",
    filename: "referees-template.csv",
    required: "name",
    optional: "email, phone, level, is_qualified (true|false), notify_enabled (true|false), is_club_member (true|false), max_age_group (e.g. U12), available_saturday (true|false), available_sunday (true|false), parent_name, parent_phone, parent_email",
    normalize: (r) => r,
  },
};

export default function UploadPanel({ loadAll, uploads, onUndo }) {
  const [kind, setKind] = useState("fixtures");
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = CONFIGS[kind];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text).map(cfg.normalize);
    setRows(parsed);
    setResult(null);
    e.target.value = ""; // allow re-selecting the same file
  };

  const submit = async () => {
    if (rows.length === 0) return toast.error("No rows parsed");
    setBusy(true);
    try {
      const { data } = await api.post(cfg.endpoint, rows);
      setResult({ kind, data });
      if (kind === "coaches") {
        toast.success(`${data.created.length} coach accounts created`);
      } else {
        const c = data.created ?? 0;
        const u = data.updated ?? 0;
        toast.success(`Uploaded — created ${c}${u ? `, updated ${u}` : ""} / ${data.total}`);
      }
      setRows([]);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(cfg.template)}`;

  return (
    <>
      <div className="card-tactical rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">Bulk upload</div>
            <p className="text-sm opacity-60 mt-1">Upload a CSV for any of the core data types. Existing records (by name / email) are updated where possible; conflicts are skipped.</p>
          </div>
          <a href={templateHref} download={cfg.filename} data-testid="upload-template-link" className="text-xs tracking-widest uppercase text-[#83B68B] hover:underline inline-flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Download {cfg.label.toLowerCase()} template
          </a>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-1">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-2">Data type</div>
            <Select value={kind} onValueChange={(v)=>{ setKind(v); setRows([]); setResult(null); }}>
              <SelectTrigger data-testid="upload-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CONFIGS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 p-4 bg-muted rounded border border-border text-xs opacity-70 font-mono">
            <div className="opacity-70 mb-1"><strong className="opacity-90">Required:</strong> {cfg.required}</div>
            <div className="opacity-70"><strong className="opacity-90">Optional:</strong> {cfg.optional}</div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <label className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 cursor-pointer" data-testid="upload-file-label">
            <Upload className="w-4 h-4" /> Choose CSV
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="upload-file-input" />
          </label>
          {rows.length > 0 && (
            <button onClick={submit} disabled={busy} data-testid="upload-submit-btn" className="px-5 py-2.5 rounded-md border border-[#83B68B] text-[#83B68B] hover:bg-[#83B68B] hover:text-white transition-colors disabled:opacity-40">
              {busy ? "Uploading…" : `Upload ${rows.length} ${cfg.label.toLowerCase()}`}
            </button>
          )}
          {rows.length > 0 && <span className="text-sm opacity-70">{rows.length} row{rows.length!==1?"s":""} parsed</span>}
        </div>

        {rows.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] tracking-widest uppercase opacity-60">
                <tr>{Object.keys(rows[0]).map(k => <th key={k} className="text-left px-3 py-2">{k}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 15).map((r, i) => (
                  <tr key={`csv-preview-${i}`} className="border-t border-border">
                    {Object.keys(rows[0]).map(k => <td key={k} className="px-3 py-2 opacity-80">{String(r[k] ?? "—")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 15 && <div className="text-xs opacity-40 mt-2">+ {rows.length - 15} more…</div>}
          </div>
        )}

        {result && (
          <div className="mt-6 p-4 bg-muted rounded border border-border">
            <div className="font-display font-bold text-lg">Result — {CONFIGS[result.kind].label}</div>
            {result.kind === "coaches" ? (
              <div className="mt-2">
                <div className="text-sm opacity-80">Created <span className="text-[#83B68B] font-bold">{result.data.created.length}</span> · Skipped <span className="text-red-500 font-bold">{result.data.skipped.length}</span> · Total {result.data.total}</div>
                {result.data.created.length > 0 && (
                  <>
                    <div className="text-xs opacity-60 mt-3 mb-1">Temporary passwords — share these securely with each coach so they can sign in:</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] tracking-widest uppercase opacity-60"><tr><th className="text-left px-3 py-1">Name</th><th className="text-left px-3 py-1">Email</th><th className="text-left px-3 py-1">Team</th><th className="text-left px-3 py-1">Temp password</th></tr></thead>
                        <tbody>
                          {result.data.created.map((c, i) => (
                            <tr key={c.email || `created-${i}`} className="border-t border-border">
                              <td className="px-3 py-1">{c.name}</td>
                              <td className="px-3 py-1 opacity-80">{c.email}</td>
                              <td className="px-3 py-1 opacity-80">{c.team_name || "—"}</td>
                              <td className="px-3 py-1 font-mono text-[#83B68B]">{c.temp_password}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {result.data.skipped.length > 0 && (
                  <div className="mt-3 text-xs opacity-60 space-y-1">
                    {result.data.skipped.map((s, i) => <div key={`skipped-${s.email || i}`}>· {s.email} — <span className="text-red-500">{s.reason}</span></div>)}
                  </div>
                )}
              </div>
            ) : result.kind === "fixtures" ? (
              <div className="text-sm opacity-80 mt-1">Batch <span className="font-mono text-xs opacity-60">{result.data.batch_id?.slice(0,8)}</span> · Created <span className="text-[#83B68B] font-bold">{result.data.created}</span> · Skipped <span className="text-red-500 font-bold">{result.data.skipped.length}</span> · Total {result.data.total}</div>
            ) : (
              <div className="text-sm opacity-80 mt-1">Created <span className="text-[#83B68B] font-bold">{result.data.created}</span> · Updated <span className="text-[#83B68B] font-bold">{result.data.updated}</span> · Skipped <span className="text-red-500 font-bold">{(result.data.skipped || []).length}</span> · Total {result.data.total}</div>
            )}
          </div>
        )}
      </div>

      <div className="card-tactical rounded-md">
        <div className="p-5 border-b border-border">
          <div className="font-display font-bold text-xl tracking-tight">Fixture upload history</div>
          <p className="text-xs opacity-60 mt-1">Only fixture uploads can be reversed (coach/team/referee uploads don't touch bookings).</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
              <tr><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Batch</th><th className="text-left px-4 py-3">By</th><th className="text-left px-4 py-3">Created</th><th className="text-left px-4 py-3">Active</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {uploads.map(u => (
                <tr key={u.id} className="border-t border-border">
                  <td className="px-4 py-3">{format(parseISO(u.created_at), "d MMM yyyy HH:mm")}</td>
                  <td className="px-4 py-3 font-mono text-xs opacity-60">{u.id.slice(0,8)}</td>
                  <td className="px-4 py-3 opacity-80">{u.admin_name}</td>
                  <td className="px-4 py-3"><span className="text-[#83B68B] font-bold">{u.created}</span> / {u.total}</td>
                  <td className="px-4 py-3">{u.active_count}</td>
                  <td className="px-4 py-3">{u.undone_at ? <span className="text-[10px] uppercase tracking-widest bg-red-500/20 text-red-500 px-2 py-1 rounded">reversed</span> : <span className="text-[10px] uppercase tracking-widest bg-[#83B68B]/20 text-[#83B68B] px-2 py-1 rounded">applied</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {!u.undone_at && u.active_count > 0 && (
                      <button onClick={()=>onUndo(u.id)} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded inline-flex items-center gap-1.5 transition-colors">
                        <Undo2 className="w-3 h-3" /> Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {uploads.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center opacity-40">No uploads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
